/**
 * Guided Tour Step Definitions
 *
 * Three tours for three user types:
 *   1. Owner / Admin  — "Welcome to Your Studio"
 *   2. Designer / Staff — "Your Daily Workflow"
 *   3. Client Portal   — "Your Jewelry Journey"
 *
 * `target` is a CSS selector for the element to highlight.
 * `null` target = centered welcome/completion card (no spotlight).
 * `route` navigates the user to that page before showing the step.
 * `placement` hints where the tooltip should appear relative to the target.
 */

export interface TourStep {
  /** CSS selector for the element to spotlight (null = centered card) */
  target: string | null
  /** Bold heading in the tooltip */
  title: string
  /** Body text — warm, non-technical, jewelry-appropriate language */
  description: string
  /** Optional tip callout in amber box */
  tip?: string
  /** Emoji or icon displayed next to the title */
  icon?: string
  /** Route to navigate to before showing this step */
  route?: string
  /** Preferred tooltip placement relative to target */
  placement?: 'top' | 'bottom' | 'left' | 'right'
}

// ─────────────────────────────────────────────────────────────
// TOUR 1 — Owner / Admin: "Welcome to Your Studio"
// Full walkthrough of every feature, including admin tools
// ─────────────────────────────────────────────────────────────

export const ownerTour: TourStep[] = [
  // ── Welcome ──
  {
    target: null,
    title: 'Welcome to Your Jewelry Studio',
    icon: '💎',
    description:
      'This is your all-in-one workspace for managing clients, tracking commissions, scheduling consultations, and growing your jewelry business. Let\'s take a quick tour — it only takes a minute.',
    tip: 'You can replay this tour anytime from the sidebar.',
  },

  // ── Studio Overview (Dashboard) ──
  {
    target: 'a[href="/"]',
    title: 'Studio Overview',
    icon: '🏠',
    description:
      'Your home base. At a glance you\'ll see total revenue, active commissions, new clients this month, and upcoming consultations. Scroll down for charts showing revenue trends, popular piece types, and client growth.',
    tip: 'Use the date picker at the top to view metrics for any time period.',
    route: '/',
    placement: 'right',
  },

  // ── KPI Cards ──
  {
    target: '[data-tour="kpi-cards"]',
    title: 'Key Numbers at a Glance',
    icon: '📊',
    description:
      'These four cards show the numbers that matter most: revenue, active commissions, total clients, and upcoming consultations. Each card shows how you\'re trending compared to the previous period.',
    route: '/',
    placement: 'bottom',
  },

  // ── Quick Actions ──
  {
    target: '[data-tour="quick-actions"]',
    title: 'Quick Actions',
    icon: '⚡',
    description:
      'Start your most common tasks right from here — create a new commission, add a client, or book a consultation. One click and you\'re on your way.',
    route: '/',
    placement: 'bottom',
  },

  // ── Clients ──
  {
    target: 'a[href="/customers"]',
    title: 'Your Clients',
    icon: '👥',
    description:
      'Your complete client directory. Each client has their contact details, design preferences (favorite metals, gemstones, styles), spending history, and every interaction you\'ve ever had.',
    tip: 'Use tags like "VIP" or "Repeat" to organise clients. Filter by status, type, or assigned designer.',
    route: '/',
    placement: 'right',
  },

  // ── Client detail preview ──
  {
    target: null,
    title: 'Inside a Client Profile',
    icon: '📋',
    description:
      'When you click on a client, you\'ll see five tabs: Overview (contact info and details), Commissions (their pieces), Consultations (meeting history), Timeline (every interaction), and Preferences (metals, styles, budget range they love).',
    tip: 'The "Invite to Portal" button gives your client their own login to view progress and approve designs.',
  },

  // ── Commissions ──
  {
    target: 'a[href="/projects"]',
    title: 'Commissions',
    icon: '💍',
    description:
      'Every piece you\'re working on, from first sketch to final polish. Commissions track jewelry type, metals, gemstones, ring size, engraving, budget, timeline, and approval status — all the details specific to fine jewelry.',
    tip: 'Switch between card view and table view using the toggle at the top.',
    route: '/',
    placement: 'right',
  },

  // ── Commission detail preview ──
  {
    target: null,
    title: 'Inside a Commission',
    icon: '🔍',
    description:
      'Each commission has six tabs: Overview (client, materials, budget), Files (design sketches, photos, documents), Timeline (stage-by-stage progress), Approvals (client sign-offs at each stage), Assets (gemstones and materials in your studio), and Tasks (to-do items for the piece).',
    tip: 'The status workflow bar at the top lets you move a piece through stages with one click: Inquiry → Consultation → Design → Approval → Production → Quality Check → Delivery → Completed.',
  },

  // ── Create Commission wizard ──
  {
    target: null,
    title: 'Creating a New Commission',
    icon: '✨',
    description:
      'The commission wizard walks you through four simple steps: 1) Select the client, 2) Enter piece details (type, metal, gemstones), 3) Set budget and timeline, 4) Review and create. Smart defaults pre-fill based on the jewelry type you choose.',
    tip: 'For an engagement ring, the wizard pre-fills 18K Gold, Round Diamond, and a 6-8 week timeline. You can change anything.',
  },

  // ── Consultations ──
  {
    target: 'a[href="/appointments"]',
    title: 'Consultations',
    icon: '📅',
    description:
      'Your consultation calendar. Schedule client meetings — in-person, video, or phone — and the system can send automatic reminders 24 hours and 2 hours before. View by week or month.',
    tip: 'Click any empty time slot on the calendar to instantly create a new consultation.',
    route: '/',
    placement: 'right',
  },

  // ── Consultation detail preview ──
  {
    target: null,
    title: 'Consultation Types',
    icon: '🤝',
    description:
      'Six consultation types are available: Initial Consultation (first meeting), Design Review (show sketches), Final Presentation (reveal the finished piece), Quick Call, Site Visit, and Follow-Up. Each can be linked to a specific commission.',
  },

  // ── Analytics ──
  {
    target: 'a[href="/analytics"]',
    title: 'Analytics & Insights',
    icon: '📈',
    description:
      'Three analytics dashboards help you understand your business: the Executive view (revenue forecasts, client health, churn risk), the Operational view (commission pipeline, team productivity), and the Explorer (custom queries on any data).',
    tip: 'The Executive dashboard is perfect for monthly business reviews. The Operational view is great for daily check-ins.',
    route: '/',
    placement: 'right',
  },

  // ── Settings ──
  {
    target: '[data-tour="settings-link"]',
    title: 'Settings & Profile',
    icon: '⚙️',
    description:
      'Manage your profile, notification preferences, and studio settings. Update your name, email, and password. Choose which email and push notifications you receive.',
    route: '/',
    placement: 'right',
  },

  // ── Developer / Admin section ──
  {
    target: '[data-tour="developer-section"]',
    title: 'Developer Tools (Admin Only)',
    icon: '🔧',
    description:
      'As the studio owner, you have access to developer tools: connect your Shopify store to sync products and orders, manage your subscription billing, view app installer metrics, and monitor integration health.',
    tip: 'The Shopify page lets you test your connection, trigger data syncs, and monitor webhooks — all without touching code.',
    route: '/',
    placement: 'right',
  },

  // ── Client Portal intro ──
  {
    target: null,
    title: 'Your Clients Get Their Own Portal',
    icon: '🌟',
    description:
      'Every client you invite gets a private portal where they can view their commissions, see progress photos, approve designs, and leave feedback. They\'ll receive email updates automatically when stages change or designs are ready for review.',
    tip: 'Invite a client from their profile page. They\'ll receive a beautiful welcome email with a login link.',
  },

  // ── Demo data ──
  {
    target: null,
    title: 'Explore with Demo Data',
    icon: '🎭',
    description:
      'We\'ve set up your studio with sample data so you can explore every feature: 5 clients (including a VIP and a prospect), commissions at every stage from inquiry to delivered, upcoming and past consultations, design approvals, and gemstone assets. Feel free to click around!',
  },

  // ── Completion ──
  {
    target: null,
    title: 'You\'re All Set!',
    icon: '🎉',
    description:
      'That\'s everything! Your jewelry studio is ready to go. Start by adding your first real client, or explore the demo data to see how everything works together. Enjoy creating beautiful pieces.',
    tip: 'To replay this tour, click "Take a Tour" in the sidebar.',
  },
]

// ─────────────────────────────────────────────────────────────
// TOUR 2 — Designer / Staff: "Your Daily Workflow"
// Focused on the features a designer uses day-to-day
// ─────────────────────────────────────────────────────────────

export const staffTour: TourStep[] = [
  // ── Welcome ──
  {
    target: null,
    title: 'Welcome to Your Workspace',
    icon: '🎨',
    description:
      'This is your daily workspace for managing commissions, connecting with clients, and keeping your schedule organised. Let\'s walk through the tools you\'ll use every day.',
    tip: 'This tour takes about 45 seconds. You can skip at any time.',
  },

  // ── Dashboard ──
  {
    target: 'a[href="/"]',
    title: 'Your Daily Overview',
    icon: '🏠',
    description:
      'Start each day here. You\'ll see your active commissions, upcoming consultations, and any items that need attention — like commissions awaiting approval or consultations in the next few hours.',
    route: '/',
    placement: 'right',
  },

  // ── Commissions ──
  {
    target: 'a[href="/projects"]',
    title: 'Your Commissions',
    icon: '💍',
    description:
      'This is where you spend most of your time. Each commission tracks the full journey of a piece: jewelry type, metals, gemstones, ring size, engraving text, design files, and stage progress. Filter by status to see what needs attention right now.',
    route: '/',
    placement: 'right',
  },

  // ── Commission stages ──
  {
    target: null,
    title: 'Commission Stages',
    icon: '📐',
    description:
      'Every commission moves through stages: Inquiry → Consultation → Design → Approval → Production → Quality Check → Delivery → Completed. You move a piece to the next stage with one click. Your client gets notified automatically at each transition.',
    tip: 'The Approvals tab inside a commission lets you send specific designs or photos for client sign-off before moving forward.',
  },

  // ── Assets tracking ──
  {
    target: null,
    title: 'Track Your Materials',
    icon: '💎',
    description:
      'The Assets tab inside each commission lets you log every physical item: gemstones (with certification numbers), precious metals, settings, and findings. Track their status — in studio, on hand, being set — and their location. Great for insurance and audit purposes.',
    tip: 'Certificate and appraisal numbers are searchable across all commissions.',
  },

  // ── Clients ──
  {
    target: 'a[href="/customers"]',
    title: 'Your Clients',
    icon: '👥',
    description:
      'Access your client directory. Each client profile shows contact details, design preferences (favourite metals, styles, budget range), and a complete history of commissions and consultations. Look at the Preferences tab to remember what they love.',
    tip: 'Tags help you organise: "VIP", "Engagement", "Repeat", "Modern", etc.',
    route: '/',
    placement: 'right',
  },

  // ── Design preferences ──
  {
    target: null,
    title: 'Client Design Preferences',
    icon: '🎯',
    description:
      'The Preferences tab on each client profile is your cheat sheet: preferred styles (Art Deco, Modern, Vintage), colour preferences (Warm Gold, Cool Platinum), material preferences (18K, Platinum, Rose Gold), budget range, and any personal notes. Update these after every consultation.',
  },

  // ── Consultations ──
  {
    target: 'a[href="/appointments"]',
    title: 'Your Consultations',
    icon: '📅',
    description:
      'Schedule client meetings directly from the calendar. Choose the type (initial consultation, design review, final presentation), set the time and duration, and link it to a commission if relevant. The calendar shows your week at a glance.',
    tip: 'You can set meetings as in-person, video call, or phone — each with location or meeting link details.',
    route: '/',
    placement: 'right',
  },

  // ── Demo data ──
  {
    target: null,
    title: 'Try It with Demo Data',
    icon: '🎭',
    description:
      'The studio has sample commissions at every stage, clients with different preferences, and consultations you can explore. Click into "Vera Gold" (a VIP client) to see a full profile, or check the commission for her sapphire pendant to see approvals and assets in action.',
  },

  // ── Completion ──
  {
    target: null,
    title: 'Ready to Create',
    icon: '✨',
    description:
      'You\'re all set! Start with your commissions list to see what\'s in progress, or check your consultations for today\'s schedule. Happy creating!',
    tip: 'Replay this tour anytime from "Take a Tour" in the sidebar.',
  },
]

// ─────────────────────────────────────────────────────────────
// TOUR 3 — Client Portal: "Your Jewelry Journey"
// What clients see when they log into their portal
// ─────────────────────────────────────────────────────────────

export const clientTour: TourStep[] = [
  // ── Welcome ──
  {
    target: null,
    title: 'Welcome to Your Jewelry Portal',
    icon: '✨',
    description:
      'This is your personal space to follow the journey of your custom jewelry. You can track progress, view design photos, approve designs, and stay connected with your designer — all in one place.',
    tip: 'You\'ll also receive email updates when your pieces reach new stages.',
  },

  // ── Dashboard ──
  {
    target: null,
    title: 'Your Active Commissions',
    icon: '💍',
    description:
      'Your dashboard shows every piece being crafted for you. Each card displays the type of jewelry, the current stage it\'s in, and when the next milestone is expected. Click any commission to see the full details.',
    route: '/client/dashboard',
  },

  // ── Progress Timeline ──
  {
    target: null,
    title: 'Follow the Journey',
    icon: '🗺️',
    description:
      'Inside each commission, you\'ll see a progress timeline showing every stage your piece goes through: Inquiry → Consultation → Design → Approval → Production → Quality Check → Delivery → Completed. Completed stages show the date they finished, and the current stage is highlighted.',
    tip: 'A glowing indicator shows exactly where your piece is right now.',
  },

  // ── Piece Details ──
  {
    target: null,
    title: 'Every Beautiful Detail',
    icon: '💎',
    description:
      'The commission detail page shows everything about your piece: the type of metal being used, the primary gemstone and its cut, ring size, any engraving text, and special design notes from your jeweler. As photos are added, you\'ll see them here too.',
  },

  // ── Approvals ──
  {
    target: null,
    title: 'Approve Your Designs',
    icon: '✅',
    description:
      'When your designer creates sketches or work-in-progress photos, they\'ll appear in your Approvals section. You can review each design and either approve it to move forward, or request changes with a note explaining what you\'d like adjusted.',
    route: '/client/approvals',
    tip: 'Your feedback goes directly to your designer — no emails or phone calls needed.',
  },

  // ── Notifications ──
  {
    target: null,
    title: 'Stay in the Loop',
    icon: '🔔',
    description:
      'You\'ll receive notifications when something happens: your piece moves to a new stage, new design photos are uploaded for review, a consultation is scheduled or changed, and of course when your piece is ready for collection!',
    tip: 'Check your profile to adjust which notifications you receive by email.',
  },

  // ── Completion ──
  {
    target: null,
    title: 'Enjoy the Experience',
    icon: '🌟',
    description:
      'That\'s it! Your portal is designed to keep you connected and excited throughout the creation of your piece. If you ever need anything, your designer is just a message away. Enjoy watching your vision come to life.',
  },
]
